import { Portal } from '@rn-primitives/portal'
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useAnimatedReaction } from 'react-native-reanimated'
import { useScrollContext } from '~/components/layout/ScrollContext'
import { cn } from '~/lib/utils'

type TriggerLayout = {
  x: number
  y: number
  width: number
  height: number
}

export function FloatingPopover({
  isOpen,
  offset = 6,
  trigger,
  children,
  className,
  contentClassName,
}: {
  isOpen: boolean
  offset?: number
  trigger: ReactNode | (() => ReactNode)
  children: ReactNode
  className?: string
  contentClassName?: string
}) {
  const portalName = useId()
  const triggerRef = useRef<View>(null)
  const [triggerLayout, setTriggerLayout] = useState<TriggerLayout | null>(null)

  const measureTrigger = useCallback(() => {
    triggerRef.current?.measureInWindow((x, y, width, height) => {
      if (width > 0 && height > 0) {
        setTriggerLayout({ x, y, width, height })
      }
    })
  }, [])

  useEffect(() => {
    if (isOpen) {
      measureTrigger()
    } else {
      setTriggerLayout(null)
    }
  }, [isOpen, measureTrigger])

  const { scrollY } = useScrollContext()
  useAnimatedReaction(() => scrollY.value, measureTrigger)

  return (
    <View className={cn('relative w-full', className)}>
      <View
        ref={triggerRef}
        onLayout={() => {
          if (isOpen) {
            requestAnimationFrame(measureTrigger)
          }
        }}>
        {typeof trigger === 'function' ? trigger() : trigger}
      </View>

      {isOpen && triggerLayout && triggerLayout.width > 0 && (
        <Portal name={portalName}>
          <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
            <View
              style={{
                position: 'absolute',
                top: triggerLayout.y + triggerLayout.height + offset,
                left: triggerLayout.x,
                width: triggerLayout.width,
                zIndex: 9999,
              }}
              className="pointer-events-auto">
              <View
                className={cn(
                  'outline-hidden w-full rounded-lg border border-border bg-popover shadow-md shadow-primary/30',
                  contentClassName
                )}>
                {children}
              </View>
            </View>
          </View>
        </Portal>
      )}
    </View>
  )
}

export default FloatingPopover
